/**
 * @file Decides what an add would do before it happens. Consumers: `useSignifyFetchJobDraft`, which
 * applies the plan, and `SignifyFetchJobPasteInput`, which renders the same numbers into the Add
 * button's label — one function, so the label can never promise what the add does not do.
 * Pure: it reads a key-owner lookup and a count, never the proxy. Within one add the first
 * occurrence of a key wins, and an add that would exceed the cap is refused whole rather than
 * truncated, because truncation drops the operator's rows without saying which.
 * `duplicates` counts only what a key already in the list rejected. An entry that yields no key at
 * all is dropped and counted nowhere, because calling it a duplicate would name a collision the
 * operator's input never had.
 */

import { SIGNIFY_FETCH_JOB_MAX_INPUTS } from '#types';
import type { SignifyFetchJobAddPlan, SignifyFetchJobDraftEntry } from '../../index.types';
import { signifyFetchJobInputKeys } from './signifyFetchJobInputKeys';

export function planSignifyFetchJobAdditions({
  entries,
  keyOwners,
  currentCount,
}: {
  entries: readonly SignifyFetchJobDraftEntry[];
  keyOwners: Readonly<Record<string, string>>;
  currentCount: number;
}): SignifyFetchJobAddPlan {
  const taken = new Set(Object.keys(keyOwners));
  const keyed = entries.flatMap<SignifyFetchJobAddPlan['accepted'][number]>((entry) => {
    const keys = signifyFetchJobInputKeys(entry);
    return keys.length === 0 ? [] : [{ ...entry, keys }];
  });
  const accepted: SignifyFetchJobAddPlan['accepted'] = [];
  keyed.forEach((entry) => {
    if (entry.keys.some((key) => taken.has(key))) return;
    entry.keys.forEach((key) => taken.add(key));
    accepted.push(entry);
  });
  const free = Math.max(0, SIGNIFY_FETCH_JOB_MAX_INPUTS - currentCount);

  return {
    accepted,
    duplicates: keyed.length - accepted.length,
    free,
    exceedsCap: accepted.length > free,
  };
}
